import { Button } from '@ahafood/ui/components/button';
import { Card } from '@ahafood/ui/components/card';
import { formatCurrency } from '@ahm/common-helpers';
import { useQueryClient } from '@tanstack/react-query';
import { getCookie } from 'cookies-next';
import { useParams } from 'next/navigation';
import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { useWebstoreSession } from '../../../components/auth/webstore-session-provider';
import type { EstimateItemFeeResponse } from '@/api/use-estimate-item-fee';
import {
  useEstimateItemFeeWS,
  type EstimateItemFeeParams as EstimateItemFeeParamsWS,
} from '@/api/use-estimate-item-ws';
import { useNavigation } from '@/components/navigation/navigation-context';
import { ROUTES } from '@/config/router';
import { useBottomPosition } from '@/hooks/use-bottom-position';
import { useRouter } from '@/i18n/routing';
import { trackEvent } from '@/lib/event-tracking';
import { useAuthStore } from '@/store';
import { useCartStore } from '@/store/cart/cart-store-provider';

export function CartFooter() {
  const router = useRouter();
  const { user } = useAuthStore();
  const branchId = useCartStore((state) => state.branchId);
  const storeSlug = useCartStore((state) => state.storeSlug);
  const currentStoreId = useCartStore((state) => state.currentStoreId);
  const { storeId } = useParams<{ storeId: string }>();
  const items = useCartStore((state) => state.items);
  const { isNavigationVisible } = useNavigation();
  const setEstimatedItem = useCartStore((state) => state.setEstimatedItem);
  const { storeData } = useWebstoreSession();
  const [totalItemsEstimatedFee, setTotalItemsEstimatedFee] = useState(0);
  const [isStoreClosed, setIsStoreClosed] = useState(false);
  const token = getCookie('ws_token');
  const tokenStr = typeof token === 'string' ? token : '';
  const queryClient = useQueryClient();
  const { bottomPosition } = useBottomPosition();
  // Debounce timer để tránh spam estimate calls
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleEstimateSuccess = useCallback(
    (data: EstimateItemFeeResponse) => {
      queryClient.setQueryData<EstimateItemFeeResponse>(
        ['estimateItemFee'],
        data
      );
      setEstimatedItem(data.total_payment);
      setTotalItemsEstimatedFee(data.total_payment);
      setIsStoreClosed(false);
      // if (mode === 'webview') {
      //   try {
      //     saveSessionBeforeExit();
      //   } catch (error) {
      //     console.error('Failed to save session after estimate:', error);
      //   }
      // }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleEstimateError = useCallback((error: any) => {
    if (error?.response?.data.code === 'STORE_CLOSED') {
      toast.error(error.response.data.title);
      setIsStoreClosed(true);
    } else {
      toast.error('Món đã hết hoặc ngưng bán. Vui lòng chọn món khác.');
    }
    setTotalItemsEstimatedFee(0);
  }, []);

  // Mutations for REST and WS
  const { mutate: estimateItemFeeWs, isPending: isEstimateLoadingWs } =
    useEstimateItemFeeWS(tokenStr, {
      onSuccess: handleEstimateSuccess,
      onError: handleEstimateError,
    });

  const itemsArray = useMemo(() => Object.values(items), [items]);
  const hasItems = itemsArray.length > 0;

  const totalQuantity = useMemo(
    () => itemsArray.reduce((sum, item) => sum + (item.quantity || 1), 0),
    [itemsArray]
  );

  // Debug effect for cart footer items (optional - remove in production)
  // useEffect(() => {
  //   console.log('Cart Footer - Items changed:', {
  //     itemsCount: itemsArray.length,
  //     hasItems,
  //     mode,
  //     storeId: storeData?._id,
  //   });
  // }, [itemsArray.length, hasItems, mode, storeData?._id]);

  // Memoize store ID để tránh re-calculation không cần thiết
  const storeIdToUse = useMemo(() => {
    // Ưu tiên sử dụng currentStoreId từ cart store
    // Fallback về storeData._id, sau đó là storeId từ URL params
    return currentStoreId
      ? Number(currentStoreId)
      : (storeData?._id ?? Number(storeId));
  }, [currentStoreId, storeData?._id, storeId]);

  // Memoize items data để tránh trigger estimate khi object reference thay đổi
  const itemsData = useMemo(() => {
    return itemsArray.map((item) => ({
      id: Number(item._id),
      quantity: item.quantity || 1,
      note: item.note,
      customizations:
        item.customizations?.map((custom) => ({
          id: custom.selectedOption?._id ?? '',
          quantity: 1,
        })) ?? [],
    }));
  }, [itemsArray]);

  // Callback để call estimate, memoized để tránh trigger không cần thiết
  const callEstimate = useCallback(() => {
    // Clear previous debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce estimate calls để tránh spam
    debounceTimerRef.current = setTimeout(() => {
      if (itemsArray.length === 0) {
        setTotalItemsEstimatedFee(0);
        setEstimatedItem(0);
        setIsStoreClosed(false);
        return;
      }

      const dataWS: EstimateItemFeeParamsWS = {
        receiver: {},
        items: itemsData,
        store_id: storeIdToUse,
      };
      estimateItemFeeWs(dataWS);
    }, 300); // Debounce 300ms
  }, [
    itemsArray.length,
    storeIdToUse,
    itemsData,
    estimateItemFeeWs,
    setEstimatedItem,
  ]);

  // Handle cart button click với tracking
  const handleCartClick = useCallback(() => {
    // Track cart button click event
    trackEvent('order_event', {
      store_id: storeIdToUse,
      store_slug: storeSlug,
      total_quantity: totalQuantity,
      total_amount: totalItemsEstimatedFee,
      button_name: 'cart_button',
      user_id: user?.cunstomerId,
    });

    router.push(ROUTES.orderCart(storeId ?? branchId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    storeIdToUse,
    storeSlug,
    totalQuantity,
    totalItemsEstimatedFee,
    router,
    storeId,
    branchId,
  ]);

  // Effect chỉ trigger khi cần thiết
  useEffect(() => {
    callEstimate();
  }, [callEstimate]); // callEstimate đã được memoized với proper dependencies

  // Cleanup debounce timer khi component unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <>
      {hasItems && (
        <>
          {/* Mobile cart footer */}
          <div className="h-16 md:hidden"></div>
          <div
            className="fixed inset-x-0 z-20 w-full max-w-lg mx-auto md:hidden"
            style={{
              bottom: isNavigationVisible ? bottomPosition : 0,
            }}
          >
            <Card className="border-0 shadow-none rounded-t-lg mx-2 p-4 bg-white">
              {isStoreClosed ? (
                <div className="w-full py-3 px-6 text-center font-bold text-red-50">
                  Quán đã đóng cửa
                </div>
              ) : (
                <Button
                  size="lg"
                  disabled={isEstimateLoadingWs}
                  className="w-full flex justify-between items-center gap-4 px-6 font-bold"
                  onClick={handleCartClick}
                >
                  <span>Giỏ hàng: {totalQuantity} món</span>
                  <span>
                    {isEstimateLoadingWs ? (
                      <span className="text-white font-normal animate-pulse">
                        Đang tính...
                      </span>
                    ) : (
                      formatCurrency(totalItemsEstimatedFee)
                    )}
                  </span>
                </Button>
              )}
            </Card>
          </div>

          {/* Desktop cart summary */}
          <div className="hidden md:block">
            <Card className="border shadow-sm rounded-lg p-6">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold">Tóm tắt đơn hàng</h3>
                  <span className="text-sm text-gray-500">
                    {totalQuantity} món
                  </span>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Tạm tính:</span>
                    <span>
                      {isEstimateLoadingWs ? (
                        <span className="animate-pulse">Đang tính...</span>
                      ) : (
                        formatCurrency(totalItemsEstimatedFee)
                      )}
                    </span>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="flex justify-between font-semibold text-lg">
                    <span>Tổng cộng:</span>
                    <span className="text-primary">
                      {isEstimateLoadingWs ? (
                        <span className="animate-pulse">Đang tính...</span>
                      ) : (
                        formatCurrency(totalItemsEstimatedFee)
                      )}
                    </span>
                  </div>
                </div>

                {isStoreClosed ? (
                  <div className="w-full py-3 px-6 text-center font-bold text-red-60 rounded-lg border border-red-50">
                    Quán đã đóng cửa
                  </div>
                ) : (
                  <Button
                    size="lg"
                    disabled={isEstimateLoadingWs}
                    className="w-full font-bold"
                    onClick={handleCartClick}
                  >
                    Tiếp tục thanh toán
                  </Button>
                )}
              </div>
            </Card>
          </div>
        </>
      )}
    </>
  );
}
